'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Forgot password states
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'code' | 'newPassword'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  // Load saved credentials if "remember me" was previously selected
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem('adminRememberEmail');
      const savedPassword = localStorage.getItem('adminRememberPassword');
      if (savedEmail && savedPassword) {
        setEmail(savedEmail);
        setPassword(savedPassword);
        setRememberMe(true);
      }
    } catch (e) {
      console.error('Error accessing localStorage:', e);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/admin-auth`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'login', email, password }),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        throw new Error('Invalid server response');
      }

      if (data.success) {
        if (rememberMe) {
          localStorage.setItem('adminRememberEmail', email);
          localStorage.setItem('adminRememberPassword', password);
        } else {
          localStorage.removeItem('adminRememberEmail');
          localStorage.removeItem('adminRememberPassword');
        }

        localStorage.setItem('adminEmail', email);
        navigate('/admin-dashboard');
      } else {
        setError(data.error || 'Giriş başarısız');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!forgotEmail) {
      setForgotError('E-posta adresi gerekli');
      return;
    }
    setForgotLoading(true);
    setForgotError('');

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/password-reset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'sendCode', email: forgotEmail, userType: 'admin' }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setForgotStep('code');
        setForgotSuccess('Doğrulama kodu e-posta adresinize gönderildi');
      } else {
        setForgotError(data.error || 'Kod gönderilemedi');
      }
    } catch (err) {
      setForgotError('Bir hata oluştu');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode) {
      setForgotError('Doğrulama kodu gerekli');
      return;
    }
    setForgotLoading(true);
    setForgotError('');

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/password-reset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'verifyCode', email: forgotEmail, code: resetCode, userType: 'admin' }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setForgotStep('newPassword');
        setForgotSuccess('Kod doğrulandı. Yeni şifrenizi belirleyin.');
      } else {
        setForgotError(data.error || 'Kod doğrulanamadı');
      }
    } catch (err) {
      setForgotError('Bir hata oluştu');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setForgotError('Tüm alanları doldurun');
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError('Şifreler eşleşmiyor');
      return;
    }
    if (newPassword.length < 6) {
      setForgotError('Şifre en az 6 karakter olmalı');
      return;
    }
    setForgotLoading(true);
    setForgotError('');

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/password-reset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'resetPassword',
            email: forgotEmail,
            code: resetCode,
            newPassword,
            userType: 'admin',
          }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setForgotSuccess('Şifreniz başarıyla güncellendi!');
        setTimeout(() => {
          closeForgotModal();
        }, 2000);
      } else {
        setForgotError(data.error || 'Şifre güncellenemedi');
      }
    } catch (err) {
      setForgotError('Bir hata oluştu');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotStep('email');
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotError('');
    setForgotSuccess('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 lg:mb-8">
          <img
            src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png"
            alt="Kartvizitler Logo"
            className="h-20 lg:h-24 w-auto object-contain mx-auto mb-4"
          />
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">
            Yönetici Girişi
          </h1>
          <p className="text-gray-600 text-xs lg:text-sm">
            Sipariş takip sistemine giriş yapın
          </p>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-gray-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                E-posta
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 lg:pl-11 pr-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="admin@example.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                Şifre
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 lg:pl-11 pr-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600">Beni Hatırla</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                Şifremi Unuttum
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-error-warning-line flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-2.5 lg:py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-sm text-sm lg:text-base"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Giriş yapılıyor...
                </span>
              ) : (
                'Giriş Yap'
              )}
            </button>
          </form>
        </div>

        <div className="text-center mt-4 lg:mt-6">
          <a
            href="/customer-login"
            className="text-gray-600 hover:text-teal-600 text-sm transition-colors cursor-pointer"
          >
            Müşteri girişi için tıklayın
          </a>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl lg:rounded-2xl w-full max-w-md p-6 lg:p-8 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900">Şifre Sıfırlama</h2>
              <button
                onClick={closeForgotModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  forgotStep === 'email' ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-600'
                }`}
              >
                1
              </div>
              <div
                className={`w-8 h-1 rounded ${
                  forgotStep !== 'email' ? 'bg-teal-500' : 'bg-gray-200'
                }`}
              ></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  forgotStep === 'code'
                    ? 'bg-teal-500 text-white'
                    : forgotStep === 'newPassword'
                    ? 'bg-teal-100 text-teal-600'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                2
              </div>
              <div
                className={`w-8 h-1 rounded ${
                  forgotStep === 'newPassword' ? 'bg-teal-500' : 'bg-gray-200'
                }`}
              ></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  forgotStep === 'newPassword' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-400'
                }`}
              >
                3
              </div>
            </div>

            {forgotError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line flex-shrink-0"></i>
                <span>{forgotError}</span>
              </div>
            )}

            {forgotSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600 flex items-center gap-2">
                <i className="ri-check-line flex-shrink-0"></i>
                <span>{forgotSuccess}</span>
              </div>
            )}

            {/* Step 1: Email */}
            {forgotStep === 'email' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  E-posta adresinizi girin, size doğrulama kodu göndereceğiz.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    E-posta
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="admin@example.com"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={forgotLoading}
                  className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 cursor-pointer text-sm"
                >
                  {forgotLoading ? 'Gönderiliyor...' : 'Kod Gönder'}
                </button>
              </div>
            )}

            {/* Step 2: Code */}
            {forgotStep === 'code' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  E-posta adresinize gönderilen 6 haneli kodu girin.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Doğrulama Kodu
                  </label>
                  <div className="relative">
                    <i className="ri-key-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) =>
                        setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm text-center tracking-widest font-mono"
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setForgotStep('email')}
                    className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all cursor-pointer text-sm"
                  >
                    Geri
                  </button>
                  <button
                    onClick={handleVerifyCode}
                    disabled={forgotLoading}
                    className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 cursor-pointer text-sm"
                  >
                    {forgotLoading ? 'Doğrulanıyor...' : 'Doğrula'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: New Password */}
            {forgotStep === 'newPassword' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Yeni şifrenizi belirleyin.</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Şifre Tekrar
                  </label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <button
                  onClick={handleResetPassword}
                  disabled={forgotLoading}
                  className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 cursor-pointer text-sm"
                >
                  {forgotLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
